import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

// ============= Shared Formatting Utilities =============

export function formatCurrency(amount: number, maximumFractionDigits: number = 0): string {
  return new Intl.NumberFormat('cs-CZ', { 
    style: 'currency', 
    currency: 'CZK',
    maximumFractionDigits 
  }).format(amount);
}

export function formatCompactCurrency(amount: number): string {
  if (amount >= 1000000) return `${(amount / 1000000).toFixed(1)}M Kč`;
  if (amount >= 1000) return `${(amount / 1000).toFixed(0)}k Kč`;
  return `${amount} Kč`;
}

export function formatDate(date: Date | string, options?: Intl.DateTimeFormatOptions): string {
  const d = typeof date === 'string' ? new Date(date) : date;
  return d.toLocaleDateString('cs-CZ', options || {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric'
  });
}

export function formatDateTime(date: Date | string): string {
  const d = typeof date === 'string' ? new Date(date) : date;
  return d.toLocaleString('cs-CZ', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
}

export function formatPercent(value: number, decimals: number = 0): string {
  return `${value.toFixed(decimals)}%`;
}

export function formatPhone(phone: string | null): string {
  if (!phone) return '-';
  const digits = phone.replace(/\D/g, '');
  if (digits.length === 9) {
    return `+420 ${digits.slice(0, 3)} ${digits.slice(3, 6)} ${digits.slice(6)}`;
  }
  if (digits.length === 12 && digits.startsWith('420')) {
    return `+${digits.slice(0, 3)} ${digits.slice(3, 6)} ${digits.slice(6, 9)} ${digits.slice(9)}`;
  }
  return phone;
}

export function calculateAPR(
  principal: number,
  totalPayable: number,
  termMonths: number,
  fees: number = 0
): number {
  const monthlyPayment = totalPayable / termMonths;
  const totalAmount = principal - fees;
  
  let rate = 0.01;
  const tolerance = 0.0000001;
  const maxIterations = 100;
  
  for (let i = 0; i < maxIterations; i++) {
    let npv = -totalAmount;
    let npvDerivative = 0;
    
    for (let t = 1; t <= termMonths; t++) {
      const discount = Math.pow(1 + rate, t);
      npv += monthlyPayment / discount;
      npvDerivative -= t * monthlyPayment / Math.pow(1 + rate, t + 1);
    }
    
    const newRate = rate - npv / npvDerivative;
    
    if (Math.abs(newRate - rate) < tolerance) {
      return (Math.pow(1 + newRate, 12) - 1) * 100;
    }
    
    rate = Math.max(0.0001, newRate);
  }
  
  const annualRate = ((totalPayable - principal) / principal) * (12 / termMonths) * 100;
  return annualRate;
}

export function generateContractNumber(prefix: string = 'SML', counter: number = 1): string {
  const year = new Date().getFullYear();
  const paddedCounter = String(counter).padStart(4, '0');
  return `${prefix}-${year}-${paddedCounter}`;
}

export function generateQRPaymentString(
  iban: string,
  amount: number,
  variableSymbol: string,
  message?: string
): string {
  const parts = [
    'SPD*1.0',
    `ACC:${iban.replace(/\s/g, '')}`,
    `AM:${amount.toFixed(2)}`,
    'CC:CZK',
    `X-VS:${variableSymbol}`,
  ];
  
  if (message) {
    parts.push(`MSG:${message.substring(0, 60)}`);
  }
  
  return parts.join('*');
}

export function validateICO(ico: string): boolean {
  if (!ico || ico.length !== 8) return false;
  
  const digits = ico.split('').map(Number);
  if (digits.some(isNaN)) return false;
  
  const weights = [8, 7, 6, 5, 4, 3, 2];
  let sum = 0;
  
  for (let i = 0; i < 7; i++) {
    sum += digits[i] * weights[i];
  }
  
  const remainder = sum % 11;
  const checkDigit = remainder === 0 ? 1 : remainder === 1 ? 0 : 11 - remainder;
  
  return digits[7] === checkDigit;
}

export function validateBirthNumber(birthNumber: string): boolean {
  const cleaned = birthNumber.replace(/\D/g, '');
  
  if (cleaned.length !== 9 && cleaned.length !== 10) return false;
  
  if (cleaned.length === 10) {
    const num = parseInt(cleaned, 10);
    return num % 11 === 0;
  }
  
  return true;
}

export function getStatusColor(status: string): 'default' | 'secondary' | 'destructive' | 'outline' {
  const colorMap: Record<string, 'default' | 'secondary' | 'destructive' | 'outline'> = {
    new: 'secondary',
    review: 'secondary',
    pending: 'secondary',
    approved: 'default',
    active: 'default',
    completed: 'outline',
    rejected: 'destructive',
    overdue: 'destructive',
    late: 'destructive',
    paid: 'default',
    due: 'secondary',
  };
  
  return colorMap[status] || 'secondary';
}

export function getStatusLabel(status: string): string {
  const labelMap: Record<string, string> = {
    new: 'Nová',
    review: 'K posouzení',
    pending: 'Čeká na schválení',
    approved: 'Schváleno',
    active: 'Aktivní',
    completed: 'Dokončeno',
    rejected: 'Zamítnuto',
    overdue: 'Po splatnosti',
    late: 'Po splatnosti',
    paid: 'Zaplaceno',
    due: 'K úhradě',
    contract_sent: 'Smlouva odeslána',
  };
  
  return labelMap[status] || status;
}
